import { v4 as uuidv4 } from 'uuid'
import { ref } from 'vue'

import { ChatFile, MIMEtypes } from '@/shared/types/files'

const MimeTypeSignatures = {
  R0lGODdh: 'image/gif',
  R0lGODlh: 'image/gif',
  iVBORw0KGgo: 'image/png',
  '/9j/': 'image/jpg',
  Qk02U: 'image/bmp',
}

/** Browser часто отдаёт пустой `file.type` для .md/.log/.pdf — дополняем из MIMEtypes. */
export const ensureFileMimeType = (file: File): File => {
  if (file.type) {
    return file
  }
  const extension = file.name.split('.').at(-1)?.toLowerCase()
  if (!extension) {
    return file
  }
  const mime = MIMEtypes.get(extension)
  if (!mime) {
    return file
  }
  return new File([file], file.name, {
    type: mime,
    lastModified: file.lastModified,
  })
}

export const convertUploadingFiles = (files: FileList, roomId: string): ChatFile[] => {
  return Array.from(files)
    .filter((file) => file.size)
    .map((rawFile) => {
      const file = ensureFileMimeType(rawFile)
      let name = `${roomId}-${uuidv4()}`
      const extension = file.name.split('.').at(-1)
      if (extension) {
        name = `${name}.${extension}`
      }

      return {
        file,
        name,
        isLoaded: ref(false),
        progress: ref(0),
        isError: ref(false),
      }})
}

export const convertUploadingFilesFromString = async (files: Array<string>, roomId: string) => {
  const chatFiles: ChatFile[] = []
  for (const pathOrFile of files) {
    try {
      const baseRegex = new RegExp('base64,', 'g')
      let path = ''
      let file: File

      if (baseRegex.test(pathOrFile)) {
        file = await createFileFromStringArray(pathOrFile)
        const { cacheDir, join } = await import('@tauri-apps/api/path')
        const pathFolder = await cacheDir()
        path = await join(pathFolder, 'tmpRtum', file.name)
      } else {
        file = await createFileFromPath(pathOrFile)
        path = pathOrFile
      }

      chatFiles.push(
        {
          file: file,
          path: path,
          name: `${roomId}-${uuidv4()}`,
          isLoaded: ref(false),
          progress: ref(0),
          isError: ref(false),
        },
      )
    } catch (err) {
      console.error(err)
    }
  }

  return chatFiles
}

const createFileFromPath = (path: string): Promise<File> => {
  return createFile(path)
}

const createFileFromStringArray = (data: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    try {
      const fileType = detectMimeTypeFromBase64(data).split('/')[1]
      const fileName = `${uuidv4()}.${fileType}`
      const dataBuffer = new Uint8Array(
        atob(data.split(',')[1]!)
          .split('')
          .map((char) => char.charCodeAt(0)),
      )

      saveFileCacheFromArray(dataBuffer, fileName)
      resolve(convertImageDataToFile(dataBuffer, fileName))
    } catch (e) {
      reject(e)
    }
  })
}

const saveFileCacheFromArray = async (data: Uint8Array, name: string): Promise<void> => {
  const { exists, mkdir, writeFile } = await import('@tauri-apps/plugin-fs')
  const { BaseDirectory } = await import('@tauri-apps/api/path')

  if (!await exists('tmpRtum', { baseDir: BaseDirectory.Cache })) {
    await mkdir('tmpRtum', { baseDir: BaseDirectory.Cache, recursive: true })
  }

  await writeFile(
    'tmpRtum/' + name,
    data,
    { baseDir: BaseDirectory.Cache },
  )
  return
}

const convertImageDataToFile = (data: Uint8Array, fileName: string): File => {
  const type = fileName.split('.')[1]
  return new File([data as BlobPart], fileName, { type: MIMEtypes.get(type!)! })
}

const detectMimeTypeFromBase64 = (base64: string) => {
  let mimeType = ''

  Object.entries(MimeTypeSignatures).some(([signature, type]) => {
    const signatureWasFound = base64.startsWith(signature)
    if (signatureWasFound) {
      mimeType = type
    } else {
      mimeType = 'image/png'
    }
    return signatureWasFound
  })

  return mimeType
}

export const createFile = (path: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const regName = /([^\\]*$)/g
    const regResult = regName.exec(path) || ''
    const fileName = regResult[0]
    const type = fileName.split('.')[1]

    import('@tauri-apps/plugin-fs').then(({ readFile }) => readFile(path).then((result) => {
      const parts = [
        new Blob([result as BlobPart], { type: MIMEtypes.get(type!)! }),
      ]
      const uploadFile = new File(parts, fileName, { type: MIMEtypes.get(type!)!, lastModified: Date.now() })
      resolve(uploadFile)
    }).catch(err => {
      reject(err)
    }))
  })
}

export const deleteDirCache = async () => {
  const { exists, remove } = await import('@tauri-apps/plugin-fs')
  const { BaseDirectory } = await import('@tauri-apps/api/path')
  if (await exists('tmpRtum', { baseDir: BaseDirectory.Cache })) {
    await remove('tmpRtum', { baseDir: BaseDirectory.Cache, recursive: true })
  }
}
